import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Promo.module.css';
import { 
    PromoCode, 
    promoService,
    PromoType,
    UpdatePromoInput
} from '../../services/promo.service';
import { authService } from '../../services/auth.service';
import { 
    getAllEnvironmentsService,
    Environment
} from '../../services/getAllEnvironment.service';

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getPromoTypeLabel = (type: PromoType) => {
    switch (type) {
        case PromoType.REGISTRATION:
            return 'Registration';
        case PromoType.DEPOSIT:
            return 'Deposit';
        // case PromoType.GENERAL:
        //     return 'General';
        default:
            return type;
    }
};

const getPromoTypeColor = (type: PromoType) => {
    switch (type) {
        case PromoType.REGISTRATION:
            return '#22c55e';
        case PromoType.DEPOSIT:
            return '#3b82f6';
        // case PromoType.GENERAL:
        //     return '#f59e0b';
        default:
            return '#666';
    }
};

const Promo = () => {
    const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasPermission, setHasPermission] = useState(false);
    const [isShopUser, setIsShopUser] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<number | ''>('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<{
        expiresAt: string;
        amount: string;
        minDeposit: string;
    }>({ expiresAt: '', amount: '', minDeposit: '' });

    useEffect(() => {
        const checkPermission = authService.hasPermission('bonus.create');
        setHasPermission(checkPermission);

        const currentUser = authService.getStoredUser();
        const isShop = currentUser?.role?.name === 'SHOP';
        setIsShopUser(isShop);

        const admin = currentUser?.role?.name === 'ADMIN';
        setIsAdmin(admin);

        // Load environments for admin users only
        if (admin && checkPermission) {
            getAllEnvironmentsService.getAllEnvironments()
                .then(setEnvironments)
                .catch(console.error);
        }
    }, []);

    const fetchPromoCodes = () => {
        setLoading(true);
        
        const currentUser = authService.getStoredUser();
        const where: any = {};

        if (currentUser?.role?.name === 'SHOP') {
            // Shop users: send only ownerId
            where.ownerId = currentUser.id;
        } else {
            // Admin users: send environmentId if selected
            if (selectedEnvironmentId) {
                where.environmentId = selectedEnvironmentId;
            }
        }

        promoService.getPromoCodes(where)
            .then(setPromoCodes)
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (hasPermission) {
            fetchPromoCodes();
        }
    }, [hasPermission]);

    useEffect(() => {
        if (hasPermission && !isShopUser) {
            fetchPromoCodes();
        }
    }, [selectedEnvironmentId]);

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this promo code?')) {
            return;
        }

        try {
            await promoService.deletePromoCode(id);
            fetchPromoCodes();
        } catch (error) {
            console.error('Error deleting promo code:', error);
            alert('Failed to delete promo code');
        }
    };

    const handleToggleActive = async (promo: PromoCode) => {
        try {
            const input: UpdatePromoInput = {
                id: promo.id,
                isActive: !promo.isActive,
            };
            
            await promoService.updatePromo(input);
            
            // Update local state
            setPromoCodes(prev => prev.map(p => 
                p.id === promo.id ? { ...p, isActive: !p.isActive } : p
            ));
        } catch (error) {
            console.error('Error updating promo code:', error);
            alert('Failed to update promo code status');
        }
    };

    const handleEditClick = (promo: PromoCode) => {
        setEditingId(promo.id);
        // Convert ISO date to datetime-local format
        const expiresAtDate = new Date(promo.expiresAt);
        const localDateTime = new Date(expiresAtDate.getTime() - expiresAtDate.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        
        setEditForm({
            expiresAt: localDateTime,
            amount: promo.amount.toString(),
            minDeposit: (promo.minDeposit || '').toString(),
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm({ expiresAt: '', amount: '', minDeposit: '' });
    };

    const handleSaveEdit = async (promoId: number) => {
        try {
            const amount = parseFloat(editForm.amount);
            if (isNaN(amount) || amount <= 0) {
                alert('Please enter a valid amount');
                return;
            }

            const input: UpdatePromoInput = {
                id: promoId,
                expiresAt: new Date(editForm.expiresAt).toISOString(),
                amount: amount,
            };

            if (editForm.minDeposit && editForm.minDeposit.trim() !== '') {
                const minDeposit = parseFloat(editForm.minDeposit);
                if (!isNaN(minDeposit) && minDeposit > 0) {
                    input.minDeposit = minDeposit;
                }
            }

            await promoService.updatePromo(input);
            
            // Update local state
            setPromoCodes(prev => prev.map(p => 
                p.id === promoId ? { 
                    ...p, 
                    expiresAt: input.expiresAt!,
                    amount: input.amount!,
                    minDeposit: input.minDeposit 
                } : p
            ));
            
            handleCancelEdit();
        } catch (error) {
            console.error('Error updating promo code:', error);
            alert('Failed to update promo code');
        }
    };

    const isExpired = (expiresAt: string) => {
        return new Date(expiresAt) < new Date();
    };

    const getReferralLink = (promo: PromoCode): string | null => {
        console.log({promo});
        const host = promo.owner?.environment?.host;
        if (!host) return null;
        return `https://${host}?referral=${promo.code}`;
    };

    const handleCopyReferral = async (promo: PromoCode) => {
        const link = getReferralLink(promo);
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            setCopiedId(promo.id);
            setTimeout(() => setCopiedId((prev) => (prev === promo.id ? null : prev)), 2000);
        } catch (err) {
            console.error('Failed to copy referral link:', err);
        }
    };

    if (!hasPermission) {
        return (
            <main className={styles.main}>
                <div className={styles.noPermission}>
                    <h2>Access Denied</h2>
                    <p>You don't have permission to manage promo codes.</p>
                    <p>Required permission: <strong>bonus.create</strong></p>
                </div>
            </main>
        );
    }


    return (
        <main className={styles.main}>
            <div className={styles.headerRow}>
                <h1 className={styles.title}>Promo Code Management</h1>
                <div className={styles.headerActions}>
                    {isAdmin && environments.length > 0 && (
                        <select
                            className={styles.formSelect}
                            value={selectedEnvironmentId}
                            onChange={(e) => setSelectedEnvironmentId(e.target.value ? Number(e.target.value) : '')}
                        >
                            <option value="">All Environments</option>
                            {environments.map((environment) => (
                                <option key={environment.id} value={environment.id}>
                                    {environment.name} - {environment.host}
                                </option>
                            ))}
                        </select>
                    )}
                    <Link to="/promo/create" className={styles.addBtn}>
                        + Create Promo Code
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className={styles.loading}>Loading promo codes...</div>
            ) : promoCodes.length === 0 ? (
                <div className={styles.noData}>No promo codes found</div>
            ) : (
                <div className={styles.promoGrid}>
                    {promoCodes.map((promo) => (
                        <div 
                            key={promo.id} 
                            className={`${styles.promoCard} ${isExpired(promo.expiresAt) ? styles.expired : ''}`}
                        >
                            <div className={styles.cardHeader}>
                                <div>
                                    <div className={styles.promoName}>{promo.name}</div>
                                    <div className={styles.promoCode}>{promo.code}</div>
                                    {/* {    console.log(getReferralLink(promo))} */}
                                    {getReferralLink(promo) && (
                                        <div className={styles.referralRow}>
                                            <span className={styles.referralLink} title={getReferralLink(promo) ?? ''}>
                                                {getReferralLink(promo)}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleCopyReferral(promo)}
                                                className={styles.copyBtn}
                                                title="Copy referral link"
                                            >
                                                {copiedId === promo.id ? 'Copied!' : 'Copy'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {editingId === promo.id ? (
                                        <>
                                            <button
                                                onClick={() => handleSaveEdit(promo.id)}
                                                className={styles.saveBtn}
                                                title="Save changes"
                                            >
                                                ✓
                                            </button>
                                            <button
                                                onClick={handleCancelEdit}
                                                className={styles.cancelEditBtn}
                                                title="Cancel editing"
                                            >
                                                ✕
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => handleEditClick(promo)}
                                                className={styles.editBtn}
                                                title="Edit promo code"
                                            >
                                                ✎
                                            </button>
                                            <button
                                                onClick={() => handleDelete(promo.id)}
                                                className={styles.deleteBtn}
                                                title="Delete promo code"
                                            >
                                                ✕
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className={styles.cardBody}>
                                {editingId === promo.id ? (
                                    <div className={styles.editFormSection}>
                                        <div className={styles.editFormGroup}>
                                            <label className={styles.editLabel}>Bonus Amount *</label>
                                            <input
                                                type="number"
                                                value={editForm.amount}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, amount: e.target.value }))}
                                                className={styles.editInput}
                                                step="0.01"
                                                min="0"
                                                required
                                            />
                                        </div>
                                        <div className={styles.editFormGroup}>
                                            <label className={styles.editLabel}>Min Deposit</label>
                                            <input
                                                type="number"
                                                value={editForm.minDeposit}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, minDeposit: e.target.value }))}
                                                className={styles.editInput}
                                                step="0.01"
                                                min="0"
                                                placeholder="Optional"
                                            />
                                        </div>
                                        <div className={styles.editFormGroup}>
                                            <label className={styles.editLabel}>Expires At *</label>
                                            <input
                                                type="datetime-local"
                                                value={editForm.expiresAt}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                                                className={styles.editInput}
                                                required
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.amountSection}>
                                            <div className={styles.amountLabel}>Bonus Amount</div>
                                            <div className={styles.amountValue}>${promo.amount}</div>
                                        </div>
                                    </>
                                )}

                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Type</span>
                                    <span 
                                        className={styles.typeChip}
                                        style={{ backgroundColor: getPromoTypeColor(promo.type) }}
                                    >
                                        {getPromoTypeLabel(promo.type)}
                                    </span>
                                </div>

                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Status</span>
                                    <label className={styles.switch}>
                                        <input
                                            type="checkbox"
                                            checked={promo.isActive ?? true}
                                            onChange={() => handleToggleActive(promo)}
                                        />
                                        <span className={styles.slider}></span>
                                    </label>
                                </div>

                                {promo.minDeposit && promo.minDeposit > 0 && (
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoLabel}>Min Deposit</span>
                                        <span className={styles.infoValue}>
                                            ${promo.minDeposit}
                                        </span>
                                    </div>
                                )}

                                {promo.owner && (
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoLabel}>Shop</span>
                                        <span className={styles.infoValue}>
                                            {promo.owner.name || promo.owner.username}
                                        </span>
                                    </div>
                                )}

                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Expires</span>
                                    <span className={`${styles.infoValue} ${isExpired(promo.expiresAt) ? styles.expiredText : ''}`}>
                                        {formatDate(promo.expiresAt)}
                                    </span>
                                </div>

                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Created</span>
                                    <span className={styles.infoValue}>
                                        {formatDate(promo.createdAt)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </main>
    );
};

export default Promo;
